/**
 * Local phone digit sanitization (country dial code is shown separately in the UI).
 */
import { countries, getCountryByCode } from '../data/countries.js';

/** @type {Record<string, { min: number, max: number }>} */
const LOCAL_LENGTH_BY_COUNTRY = {
  SN: { min: 9, max: 9 },
  FR: { min: 9, max: 9 },
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
  GB: { min: 10, max: 10 },
  DE: { min: 10, max: 11 },
};

/**
 * Strip the longest known international dial prefix from an E.164 value.
 * Use when the address country changes so old prefixes (e.g. +221) are not kept as local digits.
 * @param {string} e164Value
 * @returns {string}
 */
export function stripLocalAfterKnownDialCode(e164Value) {
  const compact = String(e164Value || '').replace(/[\s\-()]/g, '');
  if (!compact) return '';

  const withPlus = compact.startsWith('+') ? compact : `+${compact}`;
  const matched = countries
    .filter((c) => withPlus.startsWith(c.phoneCode))
    .sort((a, b) => b.phoneCode.length - a.phoneCode.length)[0];

  if (matched) {
    return withPlus.slice(matched.phoneCode.length).replace(/\D/g, '');
  }

  return withPlus.replace(/^\+/, '').replace(/\D/g, '');
}

/**
 * @param {string} digits
 * @param {{ code: string } | null | undefined} country
 * @returns {string}
 */
function applyCountryLocalNormalization(digits, country) {
  let normalized = String(digits || '').replace(/\D/g, '');
  if (country?.code === 'SN' && normalized.startsWith('0')) {
    normalized = normalized.replace(/^0+/, '');
  }
  return normalized;
}

/**
 * @param {string} rawInput
 * @param {{ code: string, phoneCode: string }} country
 * @returns {string} Local digits only (no country dial code)
 */
export function extractLocalDigits(rawInput, country) {
  const compact = String(rawInput || '').replace(/[\s\-()]/g, '');
  if (compact.startsWith('+')) {
    const withPlus = compact.startsWith('+') ? compact : `+${compact}`;
    const matched = countries
      .filter((c) => withPlus.startsWith(c.phoneCode))
      .sort((a, b) => b.phoneCode.length - a.phoneCode.length)[0];
    const local = stripLocalAfterKnownDialCode(compact);
    return applyCountryLocalNormalization(local, matched || country);
  }

  let digits = String(rawInput || '').replace(/[^\d+]/g, '');
  const dial = country.phoneCode.replace('+', '');

  if (digits.startsWith('+')) {
    digits = digits.slice(1);
  }
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  }
  if (digits.startsWith(dial)) {
    digits = digits.slice(dial.length);
  }

  return applyCountryLocalNormalization(digits, country);
}

/**
 * @param {string} rawInput
 * @param {{ code: string, phoneCode: string, name?: string }} country
 * @returns {{ localDigits: string, hint: string | null }}
 */
export function sanitizeLocalPhoneInput(rawInput, country) {
  const compact = String(rawInput || '').replace(/[\s\-().]/g, '');
  const dial = country.phoneCode.replace('+', '');
  const hadInternationalPrefix =
    compact.startsWith('+')
    || compact.startsWith(`00${dial}`)
    || (compact.startsWith(dial) && compact.length > dial.length);

  let localDigits = extractLocalDigits(rawInput, country);
  const rules = LOCAL_LENGTH_BY_COUNTRY[country.code] || { min: 4, max: 12 };

  let hint = null;
  if (hadInternationalPrefix && localDigits.length > 0) {
    hint = `N'incluez pas l'indicatif ${country.phoneCode} : il est déjà affiché à gauche.`;
  }
  if (localDigits.length > rules.max) {
    localDigits = localDigits.slice(0, rules.max);
    hint = `Maximum ${rules.max} chiffres locaux (sans l'indicatif ${country.phoneCode}).`;
  }

  return { localDigits, hint };
}

/**
 * @param {string} localDigits
 * @param {string} countryCode ISO
 * @returns {{ valid: boolean, message: string | null }}
 */
export function validateLocalPhoneForSubmit(localDigits, countryCode) {
  const country = getCountryByCode(countryCode);
  const rules = LOCAL_LENGTH_BY_COUNTRY[country.code] || { min: 4, max: 12 };
  const digits = String(localDigits || '').replace(/\D/g, '');

  if (!digits) {
    return { valid: false, message: 'Le numéro de téléphone est requis' };
  }
  if (digits.length < rules.min) {
    return {
      valid: false,
      message: `Saisissez ${rules.min} chiffres locaux pour ${country.name} (sans l'indicatif ${country.phoneCode}).`,
    };
  }
  if (digits.length > rules.max) {
    return {
      valid: false,
      message: `Le numéro ne doit pas dépasser ${rules.max} chiffres locaux (indicatif ${country.phoneCode} exclu).`,
    };
  }
  if (!/^\d+$/.test(digits)) {
    return { valid: false, message: 'Le numéro ne doit contenir que des chiffres (sans indicatif).' };
  }
  return { valid: true, message: null };
}

/**
 * @param {string} fullE164
 * @param {string} countryCode
 */
export function validateFullPhoneForSubmit(fullE164, countryCode) {
  const country = getCountryByCode(countryCode);
  const local = extractLocalDigits(fullE164, country);
  return validateLocalPhoneForSubmit(local, countryCode);
}
